// src/components/report/PartC.jsx - Updated with real images proficiency diagram
import React from 'react';

// Updated Proficiency Diagram Component with real images
const ProficiencyDiagram = () => {
    const skyImageUrl = "https://images.unsplash.com/photo-1657598339759-fd1432d833f0?fm=jpg&q=60&w=3000&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8Y2xvdWRzJTIwaW4lMjBza3l8ZW58MHx8MHx8fDA%3D";
    const mountainImageUrl = "https://upload.wikimedia.org/wikipedia/commons/thumb/3/37/Close_Up_View_of_Mount_Everest_from_Kala_Patthar_%285644_m%29_in_2023-IMG-3485.jpg/1000px-Close_Up_View_of_Mount_Everest_from_Kala_Patthar_%285644_m%29_in_2023-IMG-3485.jpg";
    const streamImageUrl = "https://img.freepik.com/free-vector/flat-design-lake-scenery_52683-76609.jpg";

    return (
        <div className="w-32 h-32 mx-auto mb-2">
            <div className="w-full h-full rounded-full overflow-hidden border-4 border-gray-600 relative">

                {/* Sky section - top third with clear boundary */}
                <div
                    className="absolute top-0 left-0 w-full border-b-2 border-gray-600"
                    style={{
                        height: 'calc(33.33% + 2px)',
                        backgroundImage: `url(${skyImageUrl})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        clipPath: 'ellipse(100% 200% at 50% 0%)'
                    }}
                >
                    <div
                        className="absolute top-2 left-1/2 transform -translate-x-1/2 font-bold text-blue-900 text-xs drop-shadow-md"
                        style={{ textShadow: '1px 1px 2px rgba(255,255,255,0.8)' }}>
                        Sky
                    </div>
                </div>

                {/* Mountain section - middle third with clear boundaries */}
                <div
                    className="absolute left-0 w-full border-b-2 border-gray-600"
                    style={{
                        top: 'calc(33.33% - 1px)',
                        height: 'calc(33.34% + 2px)',
                        backgroundImage: `url(${mountainImageUrl})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                    }}
                >
                    <div
                        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 font-bold text-white text-xs drop-shadow-lg">
                        Mountain
                    </div>
                </div>

                {/* Stream section - bottom third with clear boundary */}
                <div
                    className="absolute bottom-0 left-0 w-full"
                    style={{
                        height: 'calc(33.33% + 2px)',
                        backgroundImage: `url(${streamImageUrl})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        clipPath: 'ellipse(100% 200% at 50% 100%)'
                    }}
                >
                    <div
                        className="absolute bottom-2 left-1/2 transform -translate-x-1/2 font-bold text-green-900 text-xs drop-shadow-md"
                        style={{ textShadow: '1px 1px 2px rgba(255,255,255,0.8)' }}>
                        Stream
                    </div>
                </div>

                {/* Inner circle overlay for better definition */}
                <div
                    className="absolute inset-1 rounded-full border border-gray-400 pointer-events-none opacity-30"></div>
            </div>
        </div>
    );
};

// Proficiency Section Component with red first letter
const ProficiencySection = ({ title }) => {
    const firstLetter = title.charAt(0);
    const restOfTitle = title.slice(1);

    return (
        <div className="text-center mt-8 mb-8">
            <div className="mb-3">
                <span className="text-6xl font-bold text-red-700">
                    {firstLetter}
                </span>
                <span className="text-xl font-normal">
                    {restOfTitle}
                </span>
            </div>
            <ProficiencyDiagram />
        </div>
    );
};

// Content text component that wraps text properly
const ContentText = ({ text }) => {
    if (!text) return (
        <div className="space-y-2 mb-2">
            {Array(3).fill(0).map((_, index) => (
                <div key={index} className="border-b border-gray-600 h-4"></div>
            ))}
        </div>
    );

    return (
        <div className="mb-2">
            <div className="text-xs font-[poppins] leading-relaxed p-2  bg-opacity-70 rounded">
                {text}
            </div>
        </div>
    );
};

// Single page component for Part C
const PartCPage = ({ domains, isFirstPage }) => (
    <div className="report-page relative font-[poppins] bg-white" style={{
        width: '210mm',
        height: '297mm',
        minHeight: '297mm',
        padding: '15mm 20mm',
        margin: '0 auto',
        fontSize: '10pt',
        lineHeight: '1.4',
        color: '#333333',
        boxSizing: 'border-box',
        overflow: 'hidden'
    }}>
        {/* Orange corner triangles */}
        <div className="absolute top-0 right-0 w-0 h-0"
            style={{
                borderTop: '60px solid #F97316',
                borderLeft: '60px solid transparent'
            }}>
        </div>
        <div className="absolute bottom-0 left-0 w-0 h-0"
            style={{
                borderBottom: '60px solid #F97316',
                borderRight: '60px solid transparent'
            }}>
        </div>

        {/* Header Section - only on first page */}
        {isFirstPage && (
            <div className="text-center mb-2">
                <h1 className="text-2xl font-bold text-orange-600 mb-2">PART C</h1>
                <h2 className="text-xl font-bold text-orange-600 mb-1">SUMMARY FOR THE ACADEMIC YEAR</h2>
                <h3 className="text-lg font-bold mb-1">KEY PERFORMANCE DESCRIPTORS</h3>
                <p className="text-sm italic">(Qualitative inputs by teacher based on the student's ability)</p>
            </div>
        )}

        {/* Main Content Layout */}
        <table className="w-full h-[540px] border-2 border-gray-600 table-fixed">
            <tbody>
                <tr className="h-1/3">
                    {/* Left: Awareness */}
                    <td className="w-1/3 align-top border-r-2 border-gray-600 border-b border-gray-400 bg-white p-0">
                        <div className="flex flex-col h-full justify-center items-center">
                            <ProficiencySection title="Awareness" />
                        </div>
                    </td>
                    {/* Right: rowspan=3 for single big cell */}
                    <td className="w-2/3 align-top" rowSpan={3}>
                        <div
                            style={{
                                background: "#fee9d6",
                                border: "2px solid #EA8B35",
                                borderRadius: "6px",
                                margin: "10px 10px",
                                padding: "10px 10px",
                                minHeight: "580px",
                                // boxShadow: "7px 7px 0px 0px #fcae60"
                                // *** Removed transform/skew! ***
                            }}
                        >
                            {/* Your domain content or lines here */}
                            {domains.map((domain, index) => (
                                <div key={domain.domain_id} className="mb-2">
                                    <h4 className="font-bold text-sm mb-3">
                                        {domain.displayIndex}. {domain.domain_name}
                                    </h4>
                                    <div className="mb-2">
                                        <ContentText text={domain.awareness} />
                                    </div>
                                    <div className="mb-2">
                                        <ContentText text={domain.sensitivity} />
                                    </div>
                                    <div className="mb-2">
                                        <ContentText text={domain.creativity} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </td>
                </tr>
                <tr className="h-1/3">
                    {/* Left: Sensitivity */}
                    <td className="align-top border-r-2 border-gray-600 border-b border-gray-400 bg-white p-0">
                        <div className="flex flex-col h-full justify-center items-center">
                            <ProficiencySection title="Sensitivity" />
                        </div>
                    </td>
                </tr>
                <tr className="h-1/3">
                    {/* Left: Creativity */}
                    <td className="align-top border-r-2 border-gray-600 bg-white p-0">
                        <div className="flex flex-col h-full justify-center items-center">
                            <ProficiencySection title="Creativity" />
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>


        {/* Footer Note - only on first page */}
        {isFirstPage && (
            <div className="mt-2 mb-4">
                <p className="text-xs">
                    <span className="font-bold">NOTE:</span> A summary of the holistic development of the student needs
                    to be given at the end of an academic year in a descriptive manner in each of the five domains.
                    Essentially each of the summary should emphasize the strengths as well as the area of concerns/
                    improvements. The performances summaries should be described in terms of 3 abilities (i.e.,
                    Awareness, Sensitivity, Creativity).
                </p>
            </div>
        )}
    </div>
);

// Special page for domain 5 and positive learning habits
const PartCFinalPage = ({ domain }) => (
    <div className="report-page relative font-[poppins] bg-white" style={{
        width: '210mm',
        height: '297mm',
        minHeight: '297mm',
        padding: '15mm 20mm',
        margin: '0 auto',
        fontSize: '10pt',
        lineHeight: '1.4',
        color: '#333333',
        boxSizing: 'border-box',
        overflow: 'hidden'
    }}>
        {/* Orange corner triangles */}
        <div className="absolute top-0 right-0 w-0 h-0"
            style={{
                borderTop: '60px solid #F97316',
                borderLeft: '60px solid transparent'
            }}>
        </div>
        <div className="absolute bottom-0 left-0 w-0 h-0"
            style={{
                borderBottom: '60px solid #F97316',
                borderRight: '60px solid transparent'
            }}>
        </div>

        {/* Main Content Layout */}
        <table className="w-full h-[510px] border-2 border-gray-600 table-fixed mt-4">
            <tbody>
                {/* Awareness Row */}
                <tr style={{ height: '33.33%' }}>
                    <td className="w-1/3 align-top border-r-2 border-gray-600 border-b border-gray-400 bg-white p-0"
                        style={{ height: '33.33%' }}>
                        <div className="flex flex-col h-full justify-center items-center">
                            <ProficiencySection title="Awareness" />
                        </div>
                    </td>
                    {/* Domain 5 Content: tilted paper, single content */}
                    <td
                        className="w-2/3 align-top p-0"
                        rowSpan={3}
                        style={{ background: 'none', border: 'none', justifyContent: "center", alignItems: 'center' }}
                    >
                        <div
                            style={{
                                background: "#fee9d6",
                                border: "2px solid #EA8B35",
                                borderRadius: "6px",
                                margin: "10px 10px",
                                padding: "10px 10px",
                                minHeight: "580px",
                                // boxShadow: "7px 7px 0px 0px #fcae60"
                                // *** Removed transform/skew! ***
                            }}
                        >
                            {/* Domain 5 Content */}
                            <h4 className="font-bold text-sm mb-3">5. {domain.domain_name}</h4>

                            {/* Awareness Content */}
                            <div className="mb-2">
                                <ContentText text={domain.awareness} />
                            </div>
                            {/* Sensitivity Content */}
                            <div className="mb-2">
                                <ContentText text={domain.sensitivity} />
                            </div>
                            {/* Creativity Content */}
                            <div className="mb-4">
                                <ContentText text={domain.creativity} />
                            </div>

                            {/* 5.1. Positive Learning Habits section */}
                            <h4 className="font-bold text-sm mb-3">5.1. Positive Learning Habits</h4>
                            <div className="space-y-2 mb-2">
                                {Array(9).fill(0).map((_, index) => (
                                    <div key={index} className="border-b border-gray-600 h-4"></div>
                                ))}
                            </div>
                        </div>
                    </td>

                </tr>
                {/* Sensitivity Row */}
                <tr style={{ height: '33.33%' }}>
                    <td className="align-top border-r-2 border-gray-600 border-b border-gray-400 bg-white p-0"
                        style={{ height: '33.33%' }}>
                        <div className="flex flex-col h-full justify-center items-center">
                            <ProficiencySection title="Sensitivity" />
                        </div>
                    </td>
                </tr>
                {/* Creativity Row */}
                <tr style={{ height: '33.33%' }}>
                    <td className="align-top border-r-2 border-gray-600 bg-white p-0" style={{ height: '33.33%' }}>
                        <div className="flex flex-col h-full justify-center items-center">
                            <ProficiencySection title="Creativity" />
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
);

const PartC = ({ data }) => {
    console.log('PART C DATA: ', data);

    if (!data || data.length === 0) return <div className="report-page">Loading Part C...</div>;

    // Map API data to expected domain order
    const domainMapping = {
        "Physical Development": 1,
        "Socio-Emotional and Ethical Development": 2,
        "Cognitive Development": 3,
        "Language and Literacy Development": 4,
        "Aesthetic and Cultural Development": 5
    };

    // Sort domains by the expected order and add display index
    const sortedData = [...data].sort((a, b) => {
        const orderA = domainMapping[a.domain_name] || 999;
        const orderB = domainMapping[b.domain_name] || 999;
        return orderA - orderB;
    }).map(domain => ({
        ...domain,
        displayIndex: domainMapping[domain.domain_name]
    }));

    // Create pages with 2 domains each
    const pages = [];

    // Page 1: Domains 1-2
    if (sortedData.length >= 2) {
        pages.push({
            domains: sortedData.slice(0, 2),
            isFirstPage: true
        });
    }

    // Page 2: Domains 3-4
    if (sortedData.length >= 4) {
        pages.push({
            domains: sortedData.slice(2, 4),
            isFirstPage: false
        });
    }

    // Page 3: Domain 5 + Positive Learning Habits
    if (sortedData.length >= 5) {
        pages.push({
            domain: sortedData[4],
            isSpecialPage: true
        });
    }

    return (
        <>
            {pages.map((page, index) => {
                if (page.isSpecialPage) {
                    return (
                        <PartCFinalPage
                            key={`page-${index}`}
                            domain={page.domain}
                        />
                    );
                } else {
                    return (
                        <PartCPage
                            key={`page-${index}`}
                            domains={page.domains}
                            isFirstPage={page.isFirstPage}
                        />
                    );
                }
            })}
        </>
    );
};

export default PartC;